import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Plus, Loader2, FolderKanban, CheckCircle } from "lucide-react";

const PROJECT_TYPES = [
  "Business Setup", "Graphic Design", "SOP Creation", "Admin Support",
  "Social Media", "SEO", "Marketing", "Media Promo", "Other"
];

export { PROJECT_TYPES };

export function NewProjectDialog({ open, onOpenChange, form, setForm, clients, companies, templates, onApplyTemplate, onSubmit, saving }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Create New Project</DialogTitle>
        </DialogHeader>

        {/* Template Picker */}
        {templates.length > 0 && (
          <div className="mb-1">
            <Label className="text-xs text-neutral-500 mb-1.5 block">Quick Start from Template</Label>
            <div className="flex flex-wrap gap-1.5">
              {templates.map(t => (
                <button
                  key={t.template_id}
                  type="button"
                  onClick={() => onApplyTemplate(t)}
                  className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg border border-neutral-200 hover:border-emerald-400 hover:bg-emerald-50 transition-colors text-xs font-medium text-neutral-700 hover:text-emerald-700"
                  data-testid={`template-${t.template_id}`}
                >
                  <FolderKanban className="w-3 h-3" />
                  {t.name}
                  {t.estimated_weeks > 0 && <span className="text-[10px] text-neutral-400 ml-0.5">({t.estimated_weeks}wk)</span>}
                </button>
              ))}
            </div>
          </div>
        )}

        <div className="space-y-4 pt-1">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label>Client *</Label>
              <Select value={form.client_id} onValueChange={(v) => setForm(prev => ({ ...prev, client_id: v }))}>
                <SelectTrigger data-testid="new-project-client-select"><SelectValue placeholder="Select a client" /></SelectTrigger>
                <SelectContent>
                  {clients.map(client => (
                    <SelectItem key={client.user_id} value={client.user_id}>{client.name} ({client.email})</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Company <span className="text-neutral-400 text-xs">(optional)</span></Label>
              <Select value={form.company_id || "none"} onValueChange={(v) => setForm(prev => ({ ...prev, company_id: v === "none" ? "" : v }))}>
                <SelectTrigger data-testid="new-project-company-select"><SelectValue placeholder="No company" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No company</SelectItem>
                  {companies.map(c => <SelectItem key={c.company_id} value={c.company_id}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="space-y-2">
            <Label>Project Title *</Label>
            <Input value={form.title} onChange={(e) => setForm(prev => ({ ...prev, title: e.target.value }))}
              placeholder="e.g., Website Redesign" data-testid="new-project-title-input" />
          </div>
          <div className="space-y-2">
            <Label>Project Type *</Label>
            <Select value={form.project_type} onValueChange={(v) => setForm(prev => ({ ...prev, project_type: v }))}>
              <SelectTrigger><SelectValue placeholder="Select project type" /></SelectTrigger>
              <SelectContent>
                {PROJECT_TYPES.map(type => <SelectItem key={type} value={type}>{type}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label>Goals</Label>
            <Textarea value={form.goals} onChange={(e) => setForm(prev => ({ ...prev, goals: e.target.value }))}
              placeholder="What are the main objectives for this project?" className="min-h-16" />
          </div>
          <div className="space-y-2">
            <Label>Initial Notes</Label>
            <Textarea value={form.notes} onChange={(e) => setForm(prev => ({ ...prev, notes: e.target.value }))}
              placeholder="Any initial notes or context..." className="min-h-16" />
          </div>

          {form.milestones && form.milestones.length > 0 && (
            <div className="space-y-2">
              <Label className="flex items-center gap-1">Milestones <Badge variant="outline" className="text-[10px]">{form.milestones.length}</Badge></Label>
              <div className="border rounded-lg p-2 space-y-1 max-h-28 overflow-y-auto">
                {form.milestones.map((m, i) => (
                  <div key={i} className="flex items-center gap-2 text-xs text-neutral-600">
                    <CheckCircle className="w-3 h-3 text-neutral-300 flex-shrink-0" />
                    {m.title}
                  </div>
                ))}
              </div>
              <p className="text-[10px] text-neutral-400">From template. Milestones can be managed after creation.</p>
            </div>
          )}

          <Button onClick={onSubmit} className="w-full bg-emerald-600 hover:bg-emerald-700" disabled={saving} data-testid="create-project-submit-btn">
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />}
            Create Project
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
